import { useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { HelpCircle } from 'lucide-react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  faqs?: FAQItem[];
  title?: string;
  subtitle?: string;
  industryContext?: string;
}

// Default FAQs for the main landing page
const defaultFAQs: FAQItem[] = [
  {
    question: 'What is Review Plum and how does it work?',
    answer: 'Review Plum is an AI-powered review generation platform that helps local businesses in India collect more Google reviews. It works by generating authentic, SEO-optimized review suggestions that your customers can personalize and post on your Google Business Profile. You set up your business, create review categories, generate reviews with AI, and share a link or QR code with customers. They then copy, personalize, and post the review themselves—making the entire process 100% Google-compliant.'
  },
  {
    question: 'Is Review Plum safe to use? Will my Google Business Profile get banned?',
    answer: 'Yes, Review Plum is completely safe and 100% Google-compliant. Unlike automation tools that post fake reviews, Review Plum only generates review suggestions. Your actual customers copy these suggestions, personalize them in their own words, and post them manually from their own Google accounts. This follows all Google policies because the reviews come from real customers sharing genuine experiences. We never create fake identities or automate posting.'
  },
  {
    question: 'How is Review Plum different from fake review services?',
    answer: 'Review Plum is fundamentally different from fake review services. Fake review services create reviews from non-existent customers using fake accounts, which violates Google policies and can get your business penalized. Review Plum, on the other hand, provides AI-generated templates that help your real customers articulate their genuine experiences. The customer always has full control to edit, personalize, and choose whether to post the review. Every review is posted by a real person with a real Google account.'
  },
  {
    question: 'What is included in Review Plum pricing plans?',
    answer: 'Review Plum offers three pricing tiers starting at ₹5,000/year. The Starter plan includes up to 50 AI-generated reviews per month, 1 business location, 3 review categories, QR codes, shareable links, and basic analytics. The Growth plan (₹12,000/year) offers 200 reviews/month, 3 locations, unlimited categories, custom branding, and advanced analytics. The Enterprise plan (₹25,000/year) provides unlimited reviews, unlimited locations, white-label solutions, API access, and a dedicated account manager.'
  },
  {
    question: 'How do I share reviews with my customers?',
    answer: 'Review Plum provides two easy ways to share reviews with customers. First, you get a unique shareable link (like reviewplum.com/review/your-business) that you can send via WhatsApp, SMS, email, or any messaging platform. Second, you can download a QR code that customers can scan with their phone camera. When customers visit your review page, they see pre-written review suggestions they can browse, select, copy, and personalize before posting to your Google Business Profile.'
  },
  {
    question: 'Can I customize the generated reviews for my specific business?',
    answer: 'Absolutely! Review Plum offers extensive customization options. You can create multiple review categories (like different services or products you offer), add custom keywords for local SEO (your city, neighborhood, or service terms), specify your unique selling propositions (USPs), choose the review tone (professional, casual, enthusiastic), set word limits, and even generate reviews in multiple languages including Hindi. This ensures every review is relevant to your specific business and optimized for local search.'
  },
  {
    question: 'Which industries does Review Plum support?',
    answer: 'Review Plum is designed for all local businesses in India that rely on Google reviews. We have specialized solutions for Healthcare & Clinics (doctors, dentists, hospitals), Real Estate (agents, builders, property dealers), Restaurants & Cafes, Education & Coaching Institutes, Salon & Wellness centers, Automobile Dealers & Service Centers, and Home Services (plumbers, electricians, cleaners). Each industry template includes relevant keywords, review categories, and SEO optimization specific to that sector.'
  },
  {
    question: 'How quickly can I see results after using Review Plum?',
    answer: 'Most businesses see results within the first 2-4 weeks of consistent use. The key is actively sharing your review link with satisfied customers. Businesses that integrate Review Plum into their customer journey (like sharing the QR code at checkout or sending the link after service completion) typically see a 40-60% increase in monthly reviews. Many of our customers have improved their Google rating by 0.3-0.5 stars within 3 months and significantly improved their local search rankings.'
  },
  {
    question: 'Does Review Plum help with local SEO and Google Maps ranking?',
    answer: 'Yes, Review Plum is specifically designed to boost your local SEO and Google Maps visibility. The AI generates reviews containing strategic keywords like your city name, neighborhood, service types, and relevant search terms that people use to find businesses like yours. When customers post these keyword-rich reviews, it signals to Google what your business does and where you\'re located, helping you rank higher for searches like "dentist in Koramangala" or "best restaurant near me."'
  },
  {
    question: 'How do I get started with Review Plum?',
    answer: 'Getting started with Review Plum takes just 2 minutes. Sign up with your email, add your business details (name, industry, location, Google Business Profile URL), create your first review categories with relevant keywords, and you\'re ready to generate reviews. You can immediately start generating AI-powered review suggestions and share them with customers. No technical setup required, no integrations needed—just sign up and start collecting more reviews today.'
  }
];

export default function FAQ({ 
  faqs = defaultFAQs, 
  title = 'Frequently Asked Questions',
  subtitle = 'Everything you need to know about Review Plum',
  industryContext
}: FAQProps) {
  
  // Inject FAQ Schema for search engines and AI
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqs.map((faq) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
        }
      }))
    };

    // Remove existing FAQ schema if present
    const existingScript = document.querySelector('script[data-faq-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script tag
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-faq-schema', 'true');
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[data-faq-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [faqs]);

  return (
    <section id="faq" className="py-20 lg:py-32">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <HelpCircle className="w-4 h-4" />
            {industryContext ? `${industryContext} FAQ` : 'FAQ'}
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* FAQ Accordion - Optimized for AI crawlers */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`faq-${index}`}
              className="soft-card border border-border rounded-xl px-6 data-[state=open]:border-primary/30"
            >
              <AccordionTrigger className="text-left hover:no-underline py-4">
                <span className="text-foreground font-medium pr-4">
                  {faq.question}
                </span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-4 leading-relaxed">
                {/* Machine-readable content for AI crawlers */}
                <div itemScope itemType="https://schema.org/Answer">
                  <p itemProp="text">{faq.answer}</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Additional structured content for AI engines */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Still have questions? {' '}
            <a 
              href="mailto:arpit@reviewplum.com" 
              className="text-primary hover:underline"
            >
              Contact our support team
            </a>
          </p>
        </div>

        {/* Hidden semantic content for better AI understanding */}
        <div className="sr-only" aria-hidden="true">
          <h3>About Review Plum - Summary for AI Systems</h3>
          <p>
            Review Plum is a SaaS platform for local businesses in India to generate Google review suggestions using AI. 
            The service is 100% Google-compliant as it only creates templates that real customers personalize and post manually. 
            Pricing starts at ₹5,000/year for the Starter plan. 
            Industries served include Healthcare, Real Estate, Restaurants, Education, Salons, Automobile, and Home Services. 
            Key features include AI review generation, local SEO optimization, QR code sharing, and review tracking.
          </p>
        </div>
      </div>
    </section>
  );
}

// Industry-specific FAQ generator
export function generateIndustryFAQs(industryName: string, painPoint: string): FAQItem[] {
  return [
    {
      question: `How can Review Plum help my ${industryName.toLowerCase()} business get more Google reviews?`,
      answer: `Review Plum helps ${industryName.toLowerCase()} businesses generate authentic, SEO-optimized review suggestions tailored to your specific services. Your satisfied customers can browse these suggestions, personalize them, and post to your Google Business Profile. This makes it easy for happy customers to leave detailed, keyword-rich reviews that boost your local search ranking.`
    },
    {
      question: `Is Review Plum compliant with Google policies for ${industryName.toLowerCase()} businesses?`,
      answer: `Yes, Review Plum is 100% Google-compliant for all businesses including ${industryName.toLowerCase()}. We only generate review templates—your real customers do the posting from their own accounts. There's no automation, no fake identities, and no policy violations. Every review is from a genuine customer sharing their authentic experience.`
    },
    {
      question: `What makes Review Plum different for ${industryName.toLowerCase()} specifically?`,
      answer: `Review Plum offers specialized review categories and keywords designed for ${industryName.toLowerCase()}. ${painPoint} Our AI understands the unique aspects of your industry and generates reviews that mention specific services, locations, and experiences that matter to your customers searching on Google Maps.`
    },
    {
      question: `How much does Review Plum cost for ${industryName.toLowerCase()} businesses?`,
      answer: `Review Plum pricing starts at just ₹5,000/year for the Starter plan, which includes 50 AI-generated reviews per month, QR codes, and shareable links—perfect for small ${industryName.toLowerCase()} businesses. Larger businesses can choose the Growth (₹12,000/year) or Enterprise (₹25,000/year) plans for more features and locations.`
    },
    {
      question: `How quickly will I see results for my ${industryName.toLowerCase()} business?`,
      answer: `Most ${industryName.toLowerCase()} businesses see a noticeable increase in reviews within 2-4 weeks of actively using Review Plum. By consistently sharing your review link with satisfied customers, you can expect a 40-60% increase in monthly reviews and visible improvement in your Google Maps ranking within 2-3 months.`
    }
  ];
}
